import { AbstractBuildstep, type OptionDB } from "@project/emerge";
import type { Sides } from "@project/emerge-types";
import type * as THREE from "three"; // Needed for rotation
import { CutType } from "../enum/CutType";
import { EdgeType } from "../enum/EdgeType";
import { EndpointType } from "../enum/EndpointType";
import { LTPartType } from "../enum/PartType";
import { RectType } from "../enum/RectType";
import type { AngleSectionType } from "../interfaces/AngleSectionType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { AngleSectionDefinition } from "../parts/AngleSectionDefinition";
import { DefaultGroupName } from "../structure/Rect";
import {
  computeAngleSectionBarLengthAndShift,
  resolveAngleSectionsForEdge,
} from "./angleBarEdgeMetrics";

// --- Dependency Data Interface ---

interface AngleSectionDependencyData {
  partId: string;
  groupName: string;
  buildstepName: string;
  partKind: "AngleSection";
  type: LTPartType.AngleSection;
  cuts: {
    left: { bottom: boolean; top: boolean };
    right: { bottom: boolean; top: boolean };
  };
  dir: "x" | "y";
  length: number;
  partSelection: AngleSectionType;
  initialPosition: THREE.Vector3; // outerCenter
  side: Sides;
  orientation: number; // degrees
  minimumOffset?: number;
}

// --- Refactored Build Step ---

export class AngleSectionBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  AngleSectionDefinition, // Output type
  AngleSectionDependencyData // Dependency data type
> {
  constructor(private readonly optionDb: OptionDB) {
    super("AngleSectionBuildStep");
  }

  private computeCuts(args: {
    edge: FlightCaseBlueprint["edges"][number];
    endPoints: FlightCaseBlueprint["edges"][number]["endPoints"];
    angleSections: AngleSectionType;
  }): {
    leftCut: { bottom: boolean; top: boolean };
    rightCut: { bottom: boolean; top: boolean };
  } {
    const { edge, endPoints, angleSections } = args;

    let leftCut = { bottom: false, top: false };
    let rightCut = { bottom: false, top: false };
    const edgePointsTowardsCut =
      edge.rects[0]?.type === RectType.COVERSIDE &&
      edge.rects[1]?.type === RectType.COVERSIDE;
    const edgeIsInXDirection = edge.direction.x !== 0;
    const bottomPanelIsCover = edge.rects[0]?.type === RectType.COVERPLATE;
    const topPanelIsCover = edge.rects[1]?.type === RectType.COVERPLATE;
    const leftSideTouchesCorner = endPoints[1]?.type === EndpointType.ON_CORNER;
    const rightSideTouchesCorner =
      endPoints[0]?.type === EndpointType.ON_CORNER;

    switch (angleSections.cutType) {
      case CutType.SYMMETRIC: {
        leftCut = {
          bottom: leftSideTouchesCorner,
          top: leftSideTouchesCorner,
        };
        rightCut = {
          bottom: rightSideTouchesCorner,
          top: rightSideTouchesCorner,
        };
        break;
      }
      case CutType.ASYMMETRIC: {
        leftCut = {
          bottom:
            leftSideTouchesCorner &&
            (edgePointsTowardsCut ||
              (bottomPanelIsCover && edgeIsInXDirection)),
          top:
            leftSideTouchesCorner &&
            (edgePointsTowardsCut || (topPanelIsCover && edgeIsInXDirection)),
        };
        rightCut = {
          bottom:
            rightSideTouchesCorner &&
            (edgePointsTowardsCut ||
              (bottomPanelIsCover && edgeIsInXDirection)),
          top:
            rightSideTouchesCorner &&
            (edgePointsTowardsCut || (topPanelIsCover && edgeIsInXDirection)),
        };
        break;
      }
    }

    return { leftCut, rightCut };
  }

  private buildDependency(args: {
    edge: FlightCaseBlueprint["edges"][number];
    groupName: string;
    partId: string;
    length: number;
    shift: number;
    angleSections: AngleSectionType;
  }): AngleSectionDependencyData {
    const { edge, groupName, partId, length, shift, angleSections } = args;
    const sideForAngleSection = edge.getLeftHandSide(false);
    const orientationForAngleSection = edge.getOrientation(sideForAngleSection);
    return {
      partId,
      partKind: "AngleSection",
      type: LTPartType.AngleSection,
      groupName,
      buildstepName: this.buildstepName,
      cuts: {
        left: { bottom: false, top: false },
        right: { bottom: false, top: false },
      },
      dir: edge.dir as "x" | "y",
      length: Math.floor(length),
      partSelection: angleSections,
      initialPosition: edge.outerCenter
        .clone()
        .add(edge.direction.normalize().multiplyScalar(shift)),
      side: sideForAngleSection,
      orientation: orientationForAngleSection,
      minimumOffset: angleSections.margin,
    };
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, AngleSectionDependencyData> {
    const dependencies = new Map<string, AngleSectionDependencyData>();
    let angleSectionCounter = 0;

    for (const edge of blueprint.edges) {
      const angleSections = resolveAngleSectionsForEdge(
        this.optionDb,
        settings,
        edge,
      );

      if (edge.type === EdgeType.FROMCUT) {
        continue; // Skip edges from cuts for angle sections
      }

      const { length, shift, skipEdge } = computeAngleSectionBarLengthAndShift({
        edge,
        settings,
        angleSections,
      });
      if (skipEdge) continue;

      // Calculate cuts
      const endPoints = [edge.endPoints[0], edge.endPoints[1]] as [
        FlightCaseBlueprint["edges"][number]["endPoints"][number],
        FlightCaseBlueprint["edges"][number]["endPoints"][number],
      ];
      const { leftCut, rightCut } = this.computeCuts({
        edge,
        endPoints,
        angleSections,
      });

      // Adjust length if between two non-corner endpoints
      const leftSideTouchesCorner =
        endPoints[1]?.type === EndpointType.ON_CORNER;
      const rightSideTouchesCorner =
        endPoints[0]?.type === EndpointType.ON_CORNER;
      let adjustedLength = length;
      if (!leftSideTouchesCorner && !rightSideTouchesCorner) {
        adjustedLength--;
      }

      // Check minimum length
      if (adjustedLength < (angleSections.minimumLength ?? 0)) {
        continue;
      }

      // Generate Part ID (using rect id and edge index for uniqueness)
      const partId = `anglesection_${edge.rects[0]?.id ?? "norect"}_${edge.index}_${angleSectionCounter++}`;
      const groupName = edge.rects[0]?.group ?? DefaultGroupName;
      const dep = this.buildDependency({
        edge,
        groupName,
        partId,
        length: adjustedLength,
        shift,
        angleSections,
      });
      // attach computed cuts
      dep.cuts = { left: leftCut, right: rightCut };
      dependencies.set(partId, dep);
    }

    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: AngleSectionDependencyData,
  ): AngleSectionDefinition {
    // Calculate final rotation
    const finalRotation = this.rotateOntoSide(
      dependencies.side,
      dependencies.orientation,
    );

    // Final position calculation (apply shift) - Note: original didn't seem to apply shift to position?
    // Replicating original behavior: position is just the outerCenter.
    // If shift needs to be applied, adjust here.
    const finalPosition = dependencies.initialPosition; // .clone().add(...) if shift needed

    return {
      id: partId,
      // Spread common dependency properties
      // Explicitly list properties for clarity and type safety
      type: dependencies.type,
      group: dependencies.groupName,
      buildstepName: dependencies.buildstepName,
      cuts: dependencies.cuts,
      dir: dependencies.dir,
      length: dependencies.length,
      partSelection: dependencies.partSelection,
      position: finalPosition, // Use calculated final position
      side: dependencies.side,
      orientation: dependencies.orientation,
      minimumOffset: dependencies.minimumOffset,
      // Calculated in createPart
      rotation: finalRotation,
      // Ensure id is set if not automatically handled
      // id: partId // Assuming id might be needed by definition?
    };
  }
}
